import React from 'react';
import styles from '../insertsPages.module.css';
import { SelectionOption } from '../../../SelectionOption';
import { usePage } from '../../../../context/PageContext';
import { getPageOptions, resolveClicksToTargets } from '../../../../context/pages';

/**
 * Generic OptionSelectionPage component that renders selection options based on page configuration.
 * This replaces individual page components like AddPersonalTouch, ChooseInsertPersonalisation, etc.
 *
 * @param {Object} props - Component props.
 * @param {string} props.pageName - The current page name.
 * @param {string} [props.headerTitle] - Optional header title to display above options.
 * @returns {import('react').JSX.Element}
 */
export function OptionSelectionPage({ pageName, headerTitle, ...rest }) {
  const { getPageSelection, setPageSelection, goToPage } = usePage();
  const selectedOptionIndex = getPageSelection(pageName);

  /**
   * Handles option selection.
   * @param {Object} options - Options object.
   * @param {number} options.index - The index of the selected option.
   * @param {Object} options.option - The selected option config.
   * @returns {void}
   */
  const handleSelectOption = ({ index, option }) => {
    setPageSelection(pageName, index);

    // Navigate to the option destination, matching the config-driven footer behavior.
    const [destination] = resolveClicksToTargets(option?.clicksTo);
    if (typeof destination !== 'string' || destination.length === 0) {
      return;
    }

    setTimeout(() => {
      goToPage(destination);
    }, 300)
  };

  // Get options from centralized configuration.
  const options = getPageOptions(pageName);

  return (
    <div className={styles['inserts-pages--page']} style={{ ...rest }}>
      <div className={styles['page--container']}>
        {headerTitle && (
          <div className={styles['inserts-pages--header']}>
            <h1 className={styles['inserts-pages--title']}>{headerTitle}</h1>
          </div>
        )}
        <div className={styles['inserts-pages--options']}>
          {options.map((option, index) => {
            // Resolve subtitle - can be a string or a function.
            const subtitle = typeof option.getSubtitle === 'function' ? option.getSubtitle() : option.subtitle;

            return (
              <SelectionOption
                key={index}
                icon={option.icon}
                title={option.title}
                subtitle={subtitle}
                isSelected={selectedOptionIndex === index}
                onClick={() => handleSelectOption({ index, option })}
                clicksTo={option.clicksTo}
                disabled={option.disabled}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

